import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  AddNewButton,
  Card,
  Tag,
  Tooltip,
} from '@makeably/creativex-design-system';
import ItemsTable from 'components/molecules/ItemsTable';
import { dateOptionProps } from 'components/reporting/shared';
import { newThirdPartyHistoricalRequestPath } from 'utilities/routes';
import { toConciseCount } from 'utilities/string';
import styles from './HistoricalRequests.module.css';

const requestProps = PropTypes.shape({
  channel: PropTypes.string.isRequired,
  dateRequested: PropTypes.string.isRequired,
  dateType: PropTypes.string.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  partner: PropTypes.string.isRequired,
  requestCount: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
});

const props = {
  dateOptions: PropTypes.arrayOf(dateOptionProps).isRequired,
  historicalRequests: PropTypes.arrayOf(requestProps).isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

const headers = [
  {
    key: 'partner',
    label: 'Partner',
  },
  {
    className: styles.nameHeader,
    key: 'name',
    label: 'Request Name',
  },
  {
    key: 'channel',
    label: 'Channel',
  },
  {
    element: (
      <div className="u-flexRow u-alignCenter u-gap-8">
        Ads
        <Tooltip label="Number of ads can change as they are processed in real time." />
      </div>
    ),
    key: 'requestCount',
    label: 'Ads',
  },
  {
    key: 'date',
    label: 'Lookback Date',
  },
  {
    key: 'dateRequested',
    label: 'Date Requested',
  },
  {
    key: 'status',
    label: 'Status',
  },
];

function getStatus(isProcessing) {
  const status = isProcessing ? 'Processing...' : 'Complete';
  const color = isProcessing ? 'grey' : 'green';

  return (
    <Tag
      color={color}
      label={status}
    />
  );
}

function getItems(historicalRequests, dateOptions) {
  return historicalRequests.map((request) => {
    const matchedDateOption = dateOptions.find(
      ({ startDate, type }) => startDate === request.startDate && type === request.dateType,
    );
    const dateLabel = matchedDateOption ? matchedDateOption.label : '';

    return {
      channel: {
        value: request.channel,
      },
      date: {
        value: dateLabel || request.startDate,
      },
      dateRequested: {
        value: request.dateRequested,
      },
      id: {
        value: request.name,
      },
      name: {
        value: request.name,
      },
      partner: {
        value: request.partner,
      },
      requestCount: {
        label: toConciseCount(request.requestCount),
        value: request.requestCount,
      },
      status: {
        element: getStatus(request.isProcessing),
        value: request.isProcessing ? 'processing' : 'complete',
      },
    };
  });
}

function renderEmptyContent() {
  return (
    <div className="t-empty">
      <p>No data available</p>
    </div>
  );
}

function HistoricalRequests({
  historicalRequests,
  dateOptions,
  isAdmin,
}) {
  const items = useMemo(
    () => getItems(historicalRequests, dateOptions), [historicalRequests, dateOptions],
  );

  return (
    <Card>
      <div className="u-flexColumn u-gap-24">
        <div className="u-flexRow u-justifyBetween u-alignCenter">
          <h5>View historical data requested from your partners or send a new request</h5>
          { isAdmin && (
            <AddNewButton
              label="New Request"
              url={newThirdPartyHistoricalRequestPath()}
            />
          ) }
        </div>
        <ItemsTable
          className={styles.table}
          emptyTableContent={renderEmptyContent()}
          headers={headers}
          items={items}
        />
      </div>
    </Card>
  );
}

HistoricalRequests.propTypes = props;

export default HistoricalRequests;
